import { faEllipsisVertical, faEye, faLock, faLockOpen, faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { Batch } from '../../../models/Batch';
import { getBatchActions } from '../../../models/batchActions';
import type { BatchesModalsRef } from '../modals/BatchesModals';

interface BatchDropdownActionsProps {
  batch: Batch;
  modalsRef: RefObject<BatchesModalsRef | null>;
}

export const BatchDropdownActions = ({ batch, modalsRef }: BatchDropdownActionsProps) => {
  const { t } = useTranslation('batchesModule');
  const actions = getBatchActions(batch);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`batch-actions-${batch.id}`}>
          <FontAwesomeIcon icon={faEllipsisVertical} className="h-4 w-4" />
          <span className="sr-only">{t('table.actions')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLinkItem to={`/batches/${batch.id}`} data-testid={`batch-view-${batch.id}`}>
          <FontAwesomeIcon icon={faEye} className="mr-2 h-4 w-4" />
          {t('table.view_detail')}
        </DropdownMenuLinkItem>
        {actions.canEdit && (
          <DropdownMenuItem onClick={() => modalsRef.current?.openEdit(batch)}>
            <FontAwesomeIcon icon={faPen} className="mr-2 h-4 w-4" />
            {t('actions.edit')}
          </DropdownMenuItem>
        )}
        {actions.canUnblock && (
          <DropdownMenuItem onClick={() => modalsRef.current?.openUnblock(batch)}>
            <FontAwesomeIcon icon={faLockOpen} className="mr-2 h-4 w-4" />
            {t('actions.unblock')}
          </DropdownMenuItem>
        )}
        {actions.canBlock && (
          <DropdownMenuItem onClick={() => modalsRef.current?.openBlock(batch)}>
            <FontAwesomeIcon icon={faLock} className="mr-2 h-4 w-4" />
            {t('actions.block')}
          </DropdownMenuItem>
        )}
        {actions.canDelete && (
          <DropdownMenuItem
            onClick={() => modalsRef.current?.openDelete(batch)}
            className="text-destructive focus:text-destructive"
          >
            <FontAwesomeIcon icon={faTrash} className="mr-2 h-4 w-4" />
            {t('actions.delete')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
